import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import {
  I18nShape,
  T,
  useStaticIntl,
} from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { SvgIcon, SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { NavVertical, Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { SidebarBackButton } from '../../AdminMenu/SidebarBackButton.js'

type SidebarItem = {
  title: string
  path: string
  leadingIcon: SvgIconName
}

const sidebarItemsFactory = ({ $t }: I18nShape): SidebarItem[] => [
  {
    title: $t({ defaultMessage: 'Logos', id: 'Generics.Logos' }),
    path: adminRoutes.design.appearance.logos,
    leadingIcon: 'image-03',
  },
  {
    title: $t({ defaultMessage: 'Colors', id: 'Generics.Colors' }),
    path: adminRoutes.design.appearance.colors,
    leadingIcon: 'palette',
  },
  {
    title: $t({
      defaultMessage: 'Typographies',
      id: 'Generics.Typographies',
    }),
    path: adminRoutes.design.appearance.typography,
    leadingIcon: 'type-01',
  },
  {
    title: $t({
      defaultMessage: 'Styles',
      id: 'Generics.Styles',
    }),
    path: adminRoutes.design.appearance.styles,
    leadingIcon: 'appearance',
  },
  {
    title: $t({
      defaultMessage: 'Navigation',
      id: 'Generics.Navigation',
    }),
    path: adminRoutes.design.appearance.navigation,
    leadingIcon: 'bars',
  },
]

export function AppearanceIndexRoute() {
  return (
    <div className="flex h-full">
      <BackgroundProvider backgroundType="surface">
        <SidebarRoute />
      </BackgroundProvider>
      <div className="flex-1 overflow-hidden">
        <EmptyStateRoute />
      </div>
    </div>
  )
}

export function SidebarRoute() {
  const sidebarItems = useStaticIntl(sidebarItemsFactory)
  const { Link } = useRouter()

  return (
    <Sidebar size="lg" className="h-full">
      <Sidebar.Content>
        <div className="space-y-4">
          <SidebarBackButton href={adminRoutes.design.root} />
          <NavVertical>
            <NavVertical.Group>
              <SectionHeader
                title={
                  <T id="Generics.Appearance" defaultMessage="Appearance" />
                }
                description={
                  <T
                    id="Generics.Appearance.Description"
                    defaultMessage="Manage your brand identity and visual styling"
                  />
                }
                className="px-2 sm:ms-3"
              />
              <div className="pt-3 space-y-1 flex flex-col">
                {sidebarItems.map(item => {
                  return (
                    <NavVertical.Item
                      key={item.path}
                      as={Link}
                      href={item.path}
                      leadingIcon={<SvgIcon name={item.leadingIcon} />}
                      trailingAddon={<SvgIcon name="chevron-right" />}
                    >
                      {item.title}
                    </NavVertical.Item>
                  )
                })}
              </div>
            </NavVertical.Group>
          </NavVertical>
        </div>
      </Sidebar.Content>
    </Sidebar>
  )
}

function EmptyStateRoute() {
  return (
    <div className="flex-1 flex-col items-center gap-4 pt-20 bg-surface h-full hidden md:flex">
      <SvgIcon name="appearance" size="4x" />
      <div className="flex flex-col items-center gap-2">
        <Text variant="headingMd">
          <T
            id="Admin.Customizer.EmptyStateTitle"
            defaultMessage="Select a page to customize"
          />
        </Text>
        <Text variant="bodySm">
          <T
            id="Admin.Customizer.EmptyStateDescription"
            defaultMessage="Choose a page from the sidebar to customize its layout and content."
          />
        </Text>
      </div>
    </div>
  )
}
